export type CookieConsent = {
  version: 1;
  analytics: boolean;
  updatedAt: string;
};

const COOKIE_NAME = 'canabiz_cookie_consent';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
const OPEN_PREFERENCES_EVENT = 'canabiz:open-cookie-preferences';

const isConsent = (value: unknown): value is CookieConsent =>
  typeof value === 'object' &&
  value !== null &&
  (value as CookieConsent).version === 1 &&
  typeof (value as CookieConsent).analytics === 'boolean' &&
  typeof (value as CookieConsent).updatedAt === 'string';

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
};

export const getCookieConsent = (): CookieConsent | null => {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return isConsent(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = (): boolean => getCookieConsent()?.analytics === true;

export const saveCookieConsent = (analytics: boolean): CookieConsent => {
  const consent: CookieConsent = { version: 1, analytics, updatedAt: new Date().toISOString() };
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(consent))}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  return consent;
};

export const openCookiePreferences = (): void => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
};

export const onOpenCookiePreferences = (listener: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(OPEN_PREFERENCES_EVENT, listener);
  return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, listener);
};
